export function getDOMSelection() {
  const domSelection = window.getSelection();
  return domSelection && domSelection.rangeCount > 0 ? domSelection : null;
}

export function getDOMRange() {
  const domSelection = getDOMSelection();
  return domSelection ? domSelection.getRangeAt(0) : null;
}

export function getDocRangeSelection() {
  return domRangeToDocRange(getDOMRange());
}

export function clearAllDocRangeSelection() {
  if (getDocRangeSelection()) { window.getSelection()?.removeAllRanges(); }
}

export function domRangeToDocRange(domRange: StaticRange | Range | null) {
  if (!domRange) { return null; }

  const ancestorInlineTextNode = getAncestorInlineTextNodeFromChildNode(domRange.startContainer);
  if (!ancestorInlineTextNode) { return null; }

  let start = 0;
  let end = 0;
  if (!(ancestorInlineTextNode.dataset.isempty === "true")) {
    const startContainerOffset = findTextOffsetNodeInNode(ancestorInlineTextNode, domRange.startContainer);
    const endContainerOffset = findTextOffsetNodeInNode(ancestorInlineTextNode, domRange.endContainer);

    if (startContainerOffset === null || endContainerOffset === null) { return null; }

    start = startContainerOffset + domRange.startOffset;
    end = endContainerOffset + domRange.endOffset;
  }

  const uid = ancestorInlineTextNode.dataset.uid;
  const key = ancestorInlineTextNode.dataset.contentkey;

  if (!uid || !key) { return null; }

  return {
    range: {
      start: start,
      end: end,
    },
    uid,
    key,
  };
}

export function areEqualRangeSelections(selection1: DocSelection, selection2: DocSelection) {
  return selection1 && selection2 &&
    selection1.uid === selection2.uid &&
    selection1.key === selection2.key &&
    selection1.range && selection2.range &&
    selection1.range.start === selection2.range.start &&
    selection1.range.end === selection2.range.end;
}

function nodeTextLength(node: Node) {
  return node.nodeType === Node.TEXT_NODE && node.textContent ? node.textContent.length : 0;
}

function findTextOffsetNodeInNode(parentNode: Node, childNode: Node) {
  if (parentNode === childNode) {return 0;}

  const newWalker = document.createTreeWalker(parentNode, NodeFilter.SHOW_ALL);

  let offset = 0;
  while(newWalker.nextNode()) {
    const currentNode = newWalker.currentNode;

    if (currentNode === childNode) {
      return offset;
    } else {
      offset = offset + nodeTextLength(currentNode);
    }
  }

  return null;
}

export function isNodeInlineText(node: Node): node is HTMLElement {
  return node instanceof HTMLElement && node.classList.contains("doc-inline-text");
}

export function getAncestorInlineTextNodeFromChildNode(node: Node | null): HTMLElement | null {
  if (!node) { return null; }

  return isNodeInlineText(node) ? node : getAncestorInlineTextNodeFromChildNode(node.parentNode);
}

export function getAncestorInlineTextNodeFromUIDAndContentKey(uid: string, key: string) {
  return document.querySelector(`[data-uid="${uid}"][data-contentkey="${key}"].doc-inline-text`);
}

export function setDocSelection(docSelection: DocSelection) {
  const domRange = docSelectionToDOMRange(docSelection);
  if (domRange) { setDOMRange(domRange); }
}

export function setDOMRange(domRange: Range) {
  const domSelection = window.getSelection();

  if (!domSelection) { return domSelection; }

  domSelection.removeAllRanges();
  domSelection.addRange(domRange);
}

export function docSelectionToDOMRange(docSelection: DocSelection) {
  if (!docSelection) { return null; }

  const parentNode = getAncestorInlineTextNodeFromUIDAndContentKey(docSelection.uid, docSelection.key);

  if (parentNode) {
    const rangeStart = findNodeAndOffsetFromTextOffset(parentNode, docSelection.range.start);
    const rangeEnd = findNodeAndOffsetFromTextOffset(parentNode, docSelection.range.end);

    const domRange = document.createRange();
    domRange.setStart(...rangeStart);
    domRange.setEnd(...rangeEnd);
    return domRange;
  }

  return null;
}

function findNodeAndOffsetFromTextOffset(parentNode: Node, textOffset: number): [Node, number] {
  if (textOffset === 0) { return [parentNode, 0]; }

  const newWalker = document.createTreeWalker(parentNode, NodeFilter.SHOW_ALL);

  let currentOffset = 0;
  let currentNode;
  while(newWalker.nextNode()) {
    currentNode = newWalker.currentNode;

    const newOffset = currentOffset + nodeTextLength(currentNode);
    if (newOffset >= textOffset) {
      return [currentNode, textOffset - currentOffset];
    } else {
      currentOffset = newOffset;
    }
  }

  if (currentNode) {
    // If range exceeds end of parentNode
    // place at end of last child
    return [currentNode, nodeTextLength(currentNode)];
  } else {
    // If parent had no nodes
    // Place at start of parent
    return [parentNode, 0];
  }
}
